/**
 * Backend module IDs and UI action key → backend action string mapping.
 * Used when creating/updating permission groups so the API receives the format
 * expected by requireModulePermission (OPPORTUNITIES, PATIENTS_CONTACTS, VIEW_DETAILS, etc.).
 */

export const BACKEND_MODULES = {
  OPPORTUNITIES: "OPPORTUNITIES",
  PATIENTS_CONTACTS: "PATIENTS_CONTACTS",
  APPOINTMENTS: "APPOINTMENTS",
  OUTBOUND_CALLING: "OUTBOUND_CALLING",
  MESSAGING: "MESSAGING",
  TASKS: "TASKS",
  UNIT_POC: "UNIT_POC",
} as const;

export type BackendModuleId = keyof typeof BACKEND_MODULES;

/** Toggle-only modules: only enabled true/false, actions: [] */
export const TOGGLE_ONLY_MODULES: BackendModuleId[] = [
  BACKEND_MODULES.MESSAGING,
  BACKEND_MODULES.TASKS,
];

/**
 * UI action key → backend action string per module.
 * Keys are the form field names (e.g. "view", "create"); values are sent to the API.
 */
export const UI_TO_BACKEND_ACTION: Record<
  BackendModuleId,
  Record<string, string>
> = {
  [BACKEND_MODULES.PATIENTS_CONTACTS]: {
    view: "VIEW_DETAILS",
    create: "CREATE",
    modify: "MODIFY",
    delete: "DELETE",
    assign: "ASSIGN",
    upload: "UPLOAD",
    download: "DOWNLOAD",
  },
  [BACKEND_MODULES.OPPORTUNITIES]: {
    view: "VIEW_DETAILS",
    viewDetailsAll: "VIEW_DETAILS.ALL",
    viewDetailsTeam: "VIEW_DETAILS.TEAM",
    viewDetailsAssigned: "VIEW_DETAILS.ASSIGNED",
    create: "CREATE",
    modify: "MODIFY",
    modifyAll: "MODIFY.ALL",
    modifyOwn: "MODIFY.OWN",
    delete: "DELETE",
    reassign: "MODIFY",
    bulkEdit: "MODIFY",
    exportData: "VIEW_DETAILS",
  },
  [BACKEND_MODULES.APPOINTMENTS]: {
    view: "VIEW_DETAILS",
    create: "CREATE",
    modify: "MODIFY",
  },
  [BACKEND_MODULES.OUTBOUND_CALLING]: {
    view: "VIEW_DETAILS",
    create: "CREATE",
    modify: "MODIFY",
  },
  [BACKEND_MODULES.MESSAGING]: {},
  [BACKEND_MODULES.TASKS]: {},
  [BACKEND_MODULES.UNIT_POC]: {
    agent: "AGENT",
    spoc: "SPOC",
    manage: "MANAGE",
    configure: "CONFIGURE",
  },
};

/**
 * Map UI module id (as used in form state) to backend module id.
 * Form uses backend ids in the new implementation.
 */
export const UI_MODULE_ID_TO_BACKEND: Record<string, BackendModuleId> = {
  [BACKEND_MODULES.PATIENTS_CONTACTS]: BACKEND_MODULES.PATIENTS_CONTACTS,
  [BACKEND_MODULES.OPPORTUNITIES]: BACKEND_MODULES.OPPORTUNITIES,
  [BACKEND_MODULES.APPOINTMENTS]: BACKEND_MODULES.APPOINTMENTS,
  [BACKEND_MODULES.OUTBOUND_CALLING]: BACKEND_MODULES.OUTBOUND_CALLING,
  [BACKEND_MODULES.MESSAGING]: BACKEND_MODULES.MESSAGING,
  [BACKEND_MODULES.TASKS]: BACKEND_MODULES.TASKS,
  [BACKEND_MODULES.UNIT_POC]: BACKEND_MODULES.UNIT_POC,
  // Legacy form keys (before we switched to backend ids in form)
  patients: BACKEND_MODULES.PATIENTS_CONTACTS,
  opportunities: BACKEND_MODULES.OPPORTUNITIES,
  appointments: BACKEND_MODULES.APPOINTMENTS,
  outboundCalling: BACKEND_MODULES.OUTBOUND_CALLING,
  messaging: BACKEND_MODULES.MESSAGING,
  tasks: BACKEND_MODULES.TASKS,
  unitPoc: BACKEND_MODULES.UNIT_POC,
};

/**
 * Resolve backend action string for a module and UI action key.
 * Returns undefined if no mapping (e.g. toggle-only module).
 */
export function getBackendAction(
  backendModuleId: BackendModuleId,
  uiActionKey: string
): string | undefined {
  const map = UI_TO_BACKEND_ACTION[backendModuleId];
  return map?.[uiActionKey];
}

/**
 * Build backend modulePermissions from UI permissions map and feature toggles.
 * Uses backend module IDs and backend action strings.
 */
export function buildModulePermissionsForApi(
  permissions: Record<string, Record<string, boolean>>,
  featureToggles?: Record<string, boolean>
): Record<string, { enabled: boolean; actions: string[] }> {
  const out: Record<string, { enabled: boolean; actions: string[] }> = {};

  // Action-based modules (use backend id as key; form may use backend id or legacy id)
  // Always include each module with explicit enabled true/false and actions so false is sent to API.
  const actionModules: BackendModuleId[] = [
    BACKEND_MODULES.PATIENTS_CONTACTS,
    BACKEND_MODULES.OPPORTUNITIES,
    BACKEND_MODULES.UNIT_POC,
  ];
  for (const backendId of actionModules) {
    const legacyKey =
      backendId === BACKEND_MODULES.PATIENTS_CONTACTS
        ? "patients"
        : backendId === BACKEND_MODULES.OPPORTUNITIES
          ? "opportunities"
          : "unitPoc";
    const modPerms = permissions[backendId] ?? permissions[legacyKey];
    const actionMap = UI_TO_BACKEND_ACTION[backendId];
    const actions: string[] = [];
    if (modPerms) {
      for (const [uiKey, checked] of Object.entries(modPerms)) {
        if (checked) {
          const backendAction = actionMap[uiKey];
          if (backendAction && !actions.includes(backendAction)) {
            actions.push(backendAction);
          }
        }
      }
    }
    out[backendId] = { enabled: actions.length > 0, actions };
  }

  const toggleOnlyModules: BackendModuleId[] = [
    BACKEND_MODULES.MESSAGING,
    BACKEND_MODULES.TASKS,
  ];
  const toggleWithActionsModules: BackendModuleId[] = [
    BACKEND_MODULES.APPOINTMENTS,
    BACKEND_MODULES.OUTBOUND_CALLING,
  ];
  const threeActions = ["VIEW_DETAILS", "CREATE", "MODIFY"] as const;
  const tasksActions = ["VIEW_DETAILS", "CREATE", "MODIFY", "DELETE"] as const;

  for (const backendId of toggleWithActionsModules) {
    const legacyKey =
      backendId === BACKEND_MODULES.APPOINTMENTS
        ? "appointments"
        : "outboundCalling";
    const enabled =
      featureToggles?.[backendId] ?? featureToggles?.[legacyKey] ?? false;
    out[backendId] = { enabled, actions: enabled ? [...threeActions] : [] };
  }

  for (const backendId of toggleOnlyModules) {
    const legacyKey =
      backendId === BACKEND_MODULES.TASKS ? "tasks" : "messaging";
    const enabled =
      featureToggles?.[backendId] ?? featureToggles?.[legacyKey] ?? false;
    const actionsWhenEnabled =
      backendId === BACKEND_MODULES.TASKS ? [...tasksActions] : [...threeActions];
    out[backendId] = { enabled, actions: enabled ? actionsWhenEnabled : [] };
  }

  return out;
}

/**
 * Build form permissions + featureToggles from API modulePermissions.
 * Used when editing a permission group so the form can prefill without a separate GET.
 */
export function parseModulePermissionsFromApi(
  modulePermissions?: Record<string, { enabled: boolean; actions: string[] }>
): {
  permissions: Record<string, Record<string, boolean>>;
  featureToggles: Record<string, boolean>;
} {
  const permissions: Record<string, Record<string, boolean>> = {};
  const featureToggles: Record<string, boolean> = {};

  const actionModules: BackendModuleId[] = [
    BACKEND_MODULES.PATIENTS_CONTACTS,
    BACKEND_MODULES.OPPORTUNITIES,
    BACKEND_MODULES.UNIT_POC,
  ];
  const toggleModules: BackendModuleId[] = [
    BACKEND_MODULES.APPOINTMENTS,
    BACKEND_MODULES.OUTBOUND_CALLING,
    BACKEND_MODULES.MESSAGING,
    BACKEND_MODULES.TASKS,
  ];

  if (!modulePermissions) {
    return { permissions, featureToggles };
  }

  for (const backendId of actionModules) {
    const config = modulePermissions[backendId];
    if (!config?.actions?.length) continue;
    const uiMap = UI_TO_BACKEND_ACTION[backendId];
    const modPerms: Record<string, boolean> = {};
    for (const [uiKey, backendAction] of Object.entries(uiMap)) {
        if (config.actions.includes(backendAction)) {
          modPerms[uiKey] = true;
        }
      }
    if (Object.keys(modPerms).length > 0) {
      permissions[backendId] = modPerms;
    }
  }

  for (const backendId of toggleModules) {
    const config = modulePermissions[backendId];
    featureToggles[backendId] = config?.enabled ?? false;
  }

  return { permissions, featureToggles };
}

/** Allowed actions per module for API payload validation */
const ALLOWED_ACTIONS: Record<string, Set<string>> = {
  [BACKEND_MODULES.OPPORTUNITIES]: new Set([
    "VIEW_DETAILS",
    "VIEW_DETAILS.ALL",
    "VIEW_DETAILS.TEAM",
    "VIEW_DETAILS.ASSIGNED",
    "CREATE",
    "MODIFY",
    "MODIFY.ALL",
    "MODIFY.OWN",
    "DELETE",
  ]),
  [BACKEND_MODULES.PATIENTS_CONTACTS]: new Set([
    "VIEW_DETAILS",
    "CREATE",
    "MODIFY",
    "DELETE",
    "ASSIGN",
    "UPLOAD",
    "DOWNLOAD",
  ]),
  [BACKEND_MODULES.APPOINTMENTS]: new Set(["VIEW_DETAILS", "CREATE", "MODIFY","DELETE"]),
  [BACKEND_MODULES.OUTBOUND_CALLING]: new Set([
    "VIEW_DETAILS",
    "CREATE",
    "MODIFY",
  ]),
  [BACKEND_MODULES.MESSAGING]: new Set(["VIEW_DETAILS", "CREATE", "MODIFY"]),
  [BACKEND_MODULES.TASKS]: new Set([
    "VIEW_DETAILS",
    "CREATE",
    "MODIFY",
    "DELETE",
  ]),
  [BACKEND_MODULES.UNIT_POC]: new Set([
    "AGENT",
    "SPOC",
    "MANAGE",
    "CONFIGURE",
  ]),
};

/**
 * Validates built modulePermissions before sending to the API.
 * Returns true if valid; false otherwise.
 */
export function validateModulePermissionsForApi(
  modulePermissions: Record<string, { enabled: boolean; actions: string[] }>
): boolean {
  for (const [moduleId, config] of Object.entries(modulePermissions)) {
    const allowed = ALLOWED_ACTIONS[moduleId];
    if (!allowed) return false;
    if (config.actions.length > 0 && allowed.size === 0) {
      return false;
    }
    for (const action of config.actions) {
      if (!allowed.has(action)) return false;
    }
  }
  return true;
}
